import { AxiosError, AxiosRequestConfig } from 'axios';
import { GetServerSidePropsContext } from 'next';

// The viewer's tokens live in lscache, which only the browser reads, so they're mirrored into these
// cookies for the server: a page rendered with them is the viewer's own, its widgets carrying the
// viewer's conditional flags and continue watching.
const ACCESS_TOKEN = 'access_token';
const PROFILE_TOKEN = 'profile_access_token';
const MAX_AGE = 60 * 60 * 24 * 365;

const writeCookie = (name: string, value?: string | null) => {
  if (typeof document === 'undefined') return;
  const attributes = `path=/; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}`;
  document.cookie = value
    ? `${name}=${encodeURIComponent(value)}; max-age=${MAX_AGE}; ${attributes}`
    : `${name}=; max-age=0; ${attributes}`;
};

/**
 * Keeps the viewer's access token for the server; none removes it. Written as the token changes, not
 * after the state is saved, since a navigation right after (login, profile switch) renders with it.
 */
export const setAccessTokenCookie = (token?: string | null) => writeCookie(ACCESS_TOKEN, token);

/** Keeps the viewer's profile token for the server; none removes it. */
export const setProfileTokenCookie = (token?: string | null) => writeCookie(PROFILE_TOKEN, token);

export const clearAuthCookies = () => {
  setAccessTokenCookie(null);
  setProfileTokenCookie(null);
};

type Request = GetServerSidePropsContext['req'];

/** A server-side request's config that makes it the viewer's, from their cookies; none for a guest. */
const getViewerConfig = (req: Request): AxiosRequestConfig | undefined => {
  const token = req.cookies?.[ACCESS_TOKEN];
  if (!token) return undefined;
  const profileToken = req.cookies?.[PROFILE_TOKEN];
  return { headers: { Authorization: `Bearer ${token}`, ...(profileToken && { 'X-Profile-Token': profileToken }) } };
};

/** How the API refuses a viewer's tokens (as clientSession handles them): 401 for a session, 403 for a profile. */
const REFUSED_STATUSES = [401, 403];

/**
 * Makes a server-side request as the viewer when their cookie has a token, and `asViewer` tells whether
 * it was. Tokens the API refuses fall back to an anonymous request, so a stale cookie can't fail the page;
 * an endpoint that takes a bad token as anonymous instead (home-page does) leaves it to the client's own
 * requests to log the viewer out. The headers go on this request alone: the session serves every viewer.
 */
export async function requestAsViewer<T>(
  req: Request,
  request: (config?: AxiosRequestConfig) => Promise<T>,
): Promise<{ response: T, asViewer: boolean }> {
  const config = getViewerConfig(req);
  if (!config) return { response: await request(), asViewer: false };
  try {
    return { response: await request(config), asViewer: true };
  } catch (e) {
    if (!REFUSED_STATUSES.includes((e as AxiosError)?.response?.status ?? 0)) throw e;
    return { response: await request(), asViewer: false };
  }
}
